import type { SidebarMenuGroup } from "@/components/layouts/sidebar-layout";
import type { Collection, Extension, Form } from "@/types/entities";
import {
  UserCog,
  Users,
  Building,
  Database,
  Image,
  Settings,
  History,
  FileInput,
  Puzzle,
} from "lucide-react";
import { hasMappedExtension } from "@/extensions/registry";

export const adminMenuGroups: SidebarMenuGroup[] = [
  {
    items: [
      {
        id: "admins",
        label: "Admins",
        icon: <UserCog className="size-4" />,
        href: "/admin/dashboard/admins",
      },
      {
        id: "users",
        label: "Users",
        icon: <Users className="size-4" />,
        href: "/admin/dashboard/users",
      },
      {
        id: "tenants",
        label: "Tenants",
        icon: <Building className="size-4" />,
        href: "/admin/dashboard/tenants",
      },
    ],
  },
];

// Dynamic menu generator for tenant dashboard
export const getTenantMenus = (
  tenantSlug: string,
  collections: Collection[],
  forms: Form[] = [],
  extensions: Extension[] = []
): SidebarMenuGroup[] => {
  const groups: SidebarMenuGroup[] = [
    {
      items: [
        {
          id: "assets",
          label: "Assets",
          icon: <Image className="size-4" />,
          href: "/tenant/dashboard/assets",
        },
        {
          id: "build-history",
          label: "Build History",
          icon: <History className="size-4" />,
          href: "/tenant/dashboard/build-history",
        },
        {
          id: "settings",
          label: "Settings",
          icon: <Settings className="size-4" />,
          href: "/tenant/dashboard/settings",
        },
      ],
    }
  ];

  // Add collection data items as a separate group
  if (collections.length > 0) {
    groups.push({
      label: "Collection Data",
      items: collections.map(col => ({
        id: `collection-${col.id}`,
        label: col.label || col.name,
        icon: <Database className="size-4" />,
        href: `/tenant/dashboard/collections/${col.name}`,
      }))
    });
  }

  // Add forms as a separate group
  if (forms.length > 0) {
    groups.push({
      label: "Forms",
      items: forms.map(form => ({
        id: `form-${form.id}`,
        label: form.display_name,
        icon: <FileInput className="size-4" />,
        href: `/tenant/dashboard/forms/${form.endpoint}`,
      }))
    });
  }

  const mappedExtensions = extensions.filter((extension) => {
    return (
      extension.enabled &&
      extension.type === "cms" &&
      hasMappedExtension(tenantSlug, extension.name)
    );
  });

  if (mappedExtensions.length > 0) {
    groups.push({
      label: "Extensions",
      items: mappedExtensions.map((extension) => ({
        id: `extension-${extension.id}`,
        label: extension.display_name || extension.name,
        icon: <Puzzle className="size-4" />,
        href: `/tenant/dashboard/extensions/${extension.name}`,
      })),
    });
  }

  return groups;
};
